import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Crown, BookOpen, CaretLeft, CaretRight, CheckCircle, Circle,
  Play, FileText, Clock, Lock, ArrowRight, Cross, ChartLineUp,
  Wallet, Users, House, SignOut, Lightning, Star, GraduationCap,
  Trophy, Target, Sparkle, Video, Article, ListChecks, Download
} from '@phosphor-icons/react';
import { getCourseImage } from '../config/courseImages';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for course types
const COURSE_ICONS = {
  'digital-identity': ChartLineUp,
  'biblical-wealth': Wallet,
  'family-leadership': Users,
  'investing-foundations': ChartLineUp,
  'business-mastery': Target,
  'real-estate-investing': House
};

// Lesson type icons
const LESSON_TYPE_ICONS = {
  video: Video,
  text: Article,
  quiz: ListChecks,
  assignment: GraduationCap,
  download: Download
};

const PremiumCoursePage = () => {
  const { courseId } = useParams();
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const res = await axios.get(`${API}/api/premium-courses/${courseId}`, {
        headers: getAuthHeader()
      });
      setCourse(res.data);
      setCompletedLessons(res.data.completed_lesson_ids || []);
      
      // Set first module and lesson as active by default
      if (res.data.modules?.length > 0) {
        setActiveModule(res.data.modules[0]);
        if (res.data.modules[0].lessons?.length > 0) {
          setActiveLesson(res.data.modules[0].lessons[0]);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Inner Circle membership required');
        navigate('/inner-circle-sales');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = async () => {
    if (!activeLesson || !activeModule) return;
    
    const lessonKey = `${courseId}_${activeModule.id}_${activeLesson.id}`;
    const isCompleted = completedLessons.includes(lessonKey);
    
    try {
      if (isCompleted) {
        await axios.delete(
          `${API}/api/premium-courses/${courseId}/modules/${activeModule.id}/lessons/${activeLesson.id}/complete`,
          { headers: getAuthHeader() }
        );
        setCompletedLessons(prev => prev.filter(k => k !== lessonKey));
        toast.success('Lesson unmarked');
      } else {
        await axios.post(
          `${API}/api/premium-courses/${courseId}/modules/${activeModule.id}/lessons/${activeLesson.id}/complete`,
          {},
          { headers: getAuthHeader() }
        );
        setCompletedLessons(prev => [...prev, lessonKey]);
        toast.success('Lesson completed!');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const navigateToNext = () => {
    if (!activeModule || !activeLesson || !course) return;
    
    const currentLessonIndex = activeModule.lessons.findIndex(l => l.id === activeLesson.id);
    
    // Try next lesson in current module
    if (currentLessonIndex < activeModule.lessons.length - 1) {
      setActiveLesson(activeModule.lessons[currentLessonIndex + 1]);
      return;
    }
    
    // Try first lesson of next module
    const currentModuleIndex = course.modules.findIndex(m => m.id === activeModule.id);
    if (currentModuleIndex < course.modules.length - 1) {
      const nextModule = course.modules[currentModuleIndex + 1];
      setActiveModule(nextModule);
      if (nextModule.lessons?.length > 0) {
        setActiveLesson(nextModule.lessons[0]);
      }
    }
  };

  const CourseIcon = COURSE_ICONS[courseId] || BookOpen;
  const courseImage = getCourseImage(courseId);
  const isCurrentLessonComplete = activeModule && activeLesson && 
    completedLessons.includes(`${courseId}_${activeModule.id}_${activeLesson.id}`);

  // Get lesson type icon
  const getLessonIcon = (type) => {
    const Icon = LESSON_TYPE_ICONS[type] || Article;
    return Icon;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-[#C4A661]">Loading course...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">Course not found</p>
          <Link to="/inner-circle-hub">
            <Button className="btn-gold">Return to Inner Circle</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="nav-glass border-b border-white/10 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/inner-circle-hub" className="text-white/60 hover:text-white transition-colors">
              <CaretLeft size={24} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C4A661]/10 border border-[#C4A661]/30 flex items-center justify-center rounded-lg">
                <CourseIcon size={20} className="text-[#C4A661]" weight="fill" />
              </div>
              <div>
                <h1 className="text-[#F4F0E6] font-bold">{course.title}</h1>
                <p className="text-xs text-white/40">{course.duration}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-sm">
              <CheckCircle size={16} className="text-emerald-400" weight="fill" />
              <span className="text-white/60">
                {completedLessons.length} / {course.total_lessons} lessons
              </span>
            </div>
            <div className="h-2 w-32 bg-white/10 hidden md:block">
              <div 
                className="h-full bg-[#C4A661] transition-all"
                style={{ width: `${(completedLessons.length / course.total_lessons) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Scripture Banner */}
      <div className="bg-[#C4A661] py-2 px-6">
        <p className="text-[#0A0A0A] text-xs text-center font-medium">
          <Cross size={12} className="inline-block mr-2 -mt-0.5" weight="bold" />
          {course.scripture}
        </p>
      </div>

      {/* Course Hero Header with HD Image */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img 
          src={courseImage.header}
          alt={courseImage.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-4xl mx-auto flex items-end justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#C4A661] text-[#0A0A0A] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {course.duration}
                </span>
                <span className="text-white/60 text-sm flex items-center gap-1">
                  <BookOpen size={14} />
                  {course.total_lessons} lessons
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white">{course.title}</h2>
              <p className="text-white/60 text-sm mt-1 max-w-xl hidden md:block">{course.subtitle}</p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className="text-white/40 text-xs uppercase tracking-wider">Progress</p>
                <p className="text-[#C4A661] font-bold text-lg">
                  {Math.round((completedLessons.length / course.total_lessons) * 100)}%
                </p>
              </div>
              <div className="w-16 h-16 rounded-full border-4 border-[#C4A661]/30 flex items-center justify-center relative">
                <svg className="absolute inset-0 w-full h-full -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="#C4A661"
                    strokeWidth="4"
                    strokeDasharray={`${(completedLessons.length / course.total_lessons) * 176} 176`}
                  />
                </svg>
                <Trophy size={24} className="text-[#C4A661]" weight="fill" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Module Navigation */}
        <aside className={`${sidebarOpen ? 'w-80' : 'w-0'} bg-[#0A0A0A] border-r border-white/10 overflow-y-auto transition-all duration-300 hidden lg:block`}>
          <div className="p-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4">Course Modules</h2>
            
            <div className="space-y-2">
              {course.modules.map((module) => {
                const moduleLessons = module.lessons || [];
                const completedInModule = moduleLessons.filter(l => 
                  completedLessons.includes(`${courseId}_${module.id}_${l.id}`)
                ).length;
                const isModuleActive = activeModule?.id === module.id;
                
                return (
                  <div key={module.id} className="space-y-1">
                    <button
                      onClick={() => {
                        setActiveModule(module);
                        if (module.lessons?.length > 0) {
                          setActiveLesson(module.lessons[0]);
                        }
                      }}
                      className={`w-full text-left p-3 transition-all ${
                        isModuleActive 
                          ? 'bg-[#C4A661]/10 border-l-2 border-[#C4A661]' 
                          : 'hover:bg-white/5 border-l-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-white/40">Module {module.id}</span>
                        <span className="text-xs text-white/30">{completedInModule}/{moduleLessons.length}</span>
                      </div>
                      <p className={`text-sm font-medium ${isModuleActive ? 'text-[#C4A661]' : 'text-[#F4F0E6]'}`}>
                        {module.title}
                      </p>
                    </button>
                    
                    {/* Lessons under active module */}
                    {isModuleActive && (
                      <div className="ml-4 space-y-1">
                        {moduleLessons.map((lesson) => {
                          const lessonKey = `${courseId}_${module.id}_${lesson.id}`;
                          const isLessonComplete = completedLessons.includes(lessonKey);
                          const isLessonActive = activeLesson?.id === lesson.id;
                          
                          return (
                            <button
                              key={lesson.id}
                              onClick={() => setActiveLesson(lesson)}
                              className={`w-full text-left p-2 flex items-center gap-2 transition-all ${
                                isLessonActive 
                                  ? 'bg-white/10 text-[#F4F0E6]' 
                                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                              }`}
                            >
                              {isLessonComplete ? (
                                <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" weight="fill" />
                              ) : (
                                <Circle size={14} className="text-white/30 flex-shrink-0" />
                              )}
                              <span className="text-xs truncate">{lesson.title}</span>
                              {lesson.type === 'video' && (
                                <Play size={10} className="ml-auto flex-shrink-0 text-white/30" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Main Content - Light Background */}
        <main className="flex-1 overflow-y-auto bg-[#F5F5F5]">
          {activeLesson ? (
            <div className="max-w-4xl mx-auto p-6 md:p-10">
              {/* Lesson Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 text-xs text-[#525252] mb-3">
                  <span className="flex items-center gap-1">
                    <BookOpen size={12} className="text-[#C4A661]" />
                    Module {activeModule?.id}
                  </span>
                  <CaretRight size={12} />
                  <span>Lesson {activeLesson.id}</span>
                </div>
                
                {/* Lesson type badge */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    activeLesson.type === 'video' 
                      ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30' 
                      : 'bg-gradient-to-br from-[#C4A661]/20 to-amber-600/10 border border-[#C4A661]/30'
                  }`}>
                    {(() => {
                      const LessonIcon = getLessonIcon(activeLesson.type);
                      return <LessonIcon size={24} className={activeLesson.type === 'video' ? 'text-purple-500' : 'text-[#C4A661]'} weight="fill" />;
                    })()}
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0A0A0A]">
                      {activeLesson.title}
                    </h2>
                    <div className="flex items-center gap-4 text-sm text-[#525252] mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        activeLesson.type === 'video' 
                          ? 'bg-purple-500/20 text-purple-600' 
                          : 'bg-[#C4A661]/20 text-[#8B7355]'
                      }`}>
                        {activeLesson.type === 'video' ? 'Video Lesson' : 'Reading'}
                      </span>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{activeLesson.duration}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lesson Content - LIGHT SECTION */}
              <div className="bg-[#FDFDFD] border border-[#E5E5E5] rounded-xl p-6 md:p-8 mb-8 relative overflow-hidden">
                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#C4A661]/5 to-transparent pointer-events-none" />
                
                <div className="prose max-w-none relative">
                  {activeLesson.content.split('\n\n').map((paragraph, i) => {
                    // Handle headers
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={i} className="text-lg font-bold text-[#0A0A0A] mt-8 mb-4 flex items-center gap-2">
                          <Sparkle size={18} className="text-[#C4A661]" weight="fill" />
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    // Handle inline bold headers
                    if (paragraph.startsWith('**') && paragraph.includes(':**')) {
                      const [header, ...rest] = paragraph.split(':**');
                      return (
                        <div key={i} className="my-4">
                          <h4 className="text-base font-bold text-[#0A0A0A] mb-2 flex items-center gap-2">
                            <Target size={16} className="text-[#C4A661]" />
                            {header.replace(/\*\*/g, '')}:
                          </h4>
                          <p className="text-[#525252] leading-relaxed pl-6">{rest.join(':').replace(/\*\*/g, '')}</p>
                        </div>
                      );
                    }
                    // Handle bullet lists
                    if (paragraph.includes('- ')) {
                      const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                      return (
                        <ul key={i} className="space-y-3 my-6 pl-2">
                          {items.map((item, j) => (
                            <li key={j} className="flex items-start gap-3 text-[#525252]">
                              <CheckCircle size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" weight="fill" />
                              <span className="leading-relaxed">{item.replace('- ', '')}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    // Handle numbered lists
                    if (/^\d\./.test(paragraph)) {
                      const items = paragraph.split('\n').filter(line => /^\d\./.test(line));
                      return (
                        <ol key={i} className="space-y-4 my-6">
                          {items.map((item, j) => (
                            <li key={j} className="flex items-start gap-4 text-[#525252]">
                              <span className="w-8 h-8 rounded-full bg-[#C4A661]/10 border border-[#C4A661]/30 flex items-center justify-center text-[#C4A661] font-bold text-sm flex-shrink-0">
                                {j + 1}
                              </span>
                              <span className="leading-relaxed pt-1">{item.replace(/^\d\.\s*/, '')}</span>
                            </li>
                          ))}
                        </ol>
                      );
                    }
                    // Handle scripture quotes
                    if (paragraph.includes('Scripture:') || paragraph.includes('—')) {
                      return (
                        <blockquote key={i} className="border-l-2 border-[#C4A661] pl-4 py-2 my-4 italic text-white/60">
                          {paragraph}
                        </blockquote>
                      );
                    }
                    // Handle action steps
                    if (paragraph.startsWith('**Action Step:**') || paragraph.startsWith('Action Step:')) {
                      return (
                        <div key={i} className="bg-[#C4A661]/10 border border-[#C4A661]/30 p-4 my-6 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightning size={18} className="text-[#C4A661]" weight="fill" />
                            <span className="font-bold text-[#C4A661]">Action Step</span>
                          </div>
                          <p className="text-[#0A0A0A]">
                            {paragraph.replace(/^\*\*Action Step:\*\*\s*/, '').replace(/^Action Step:\s*/, '')}
                          </p>
                        </div>
                      );
                    }
                    // Regular paragraph
                    return (
                      <p key={i} className="text-[#525252] leading-relaxed my-4">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <Button
                  onClick={handleLessonComplete}
                  className={`${isCurrentLessonComplete ? 'btn-outline' : 'btn-gold'} w-full sm:w-auto`}
                  data-testid="complete-lesson-btn"
                >
                  {isCurrentLessonComplete ? (
                    <>
                      <CheckCircle size={18} className="mr-2" weight="fill" />
                      Completed - Click to Undo
                    </>
                  ) : (
                    <>
                      <Circle size={18} className="mr-2" />
                      Mark as Complete
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={navigateToNext}
                  className="btn-outline w-full sm:w-auto"
                  data-testid="next-lesson-btn"
                >
                  Next Lesson
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <BookOpen size={48} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50">Select a lesson to begin</p>
              </div>
            </div>
          )}
        </main>

        {/* Mobile Module Selector */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 p-4 z-30">
          <select
            value={activeModule?.id || ''}
            onChange={(e) => {
              const module = course.modules.find(m => m.id === parseInt(e.target.value));
              if (module) {
                setActiveModule(module);
                if (module.lessons?.length > 0) {
                  setActiveLesson(module.lessons[0]);
                }
              }
            }}
            className="w-full bg-[#121212] border border-white/10 text-[#F4F0E6] p-3 mb-2"
          >
            {course.modules.map((module) => (
              <option key={module.id} value={module.id}>
                Module {module.id}: {module.title}
              </option>
            ))}
          </select>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {activeModule?.lessons?.map((lesson) => {
              const lessonKey = `${courseId}_${activeModule.id}_${lesson.id}`;
              const isComplete = completedLessons.includes(lessonKey);
              const isActive = activeLesson?.id === lesson.id;
              
              return (
                <button
                  key={lesson.id}
                  onClick={() => setActiveLesson(lesson)}
                  className={`flex-shrink-0 px-3 py-2 text-xs flex items-center gap-1 ${
                    isActive 
                      ? 'bg-[#C4A661] text-[#0A0A0A]' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {isComplete && <CheckCircle size={12} weight="fill" className={isActive ? 'text-[#0A0A0A]' : 'text-emerald-400'} />}
                  L{lesson.id}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumCoursePage;
